import { useState } from 'react';
import { Button, ConfirmActionModal } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { isApiError } from '@/services/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ProductAddOnGroupDataTable } from './ProductAddOnGroupDataTable';
import { AddOnGroupCombobox } from './AddOnGroupCombobox';
import { useProductAddOnGroupsQuery } from '../hooks/useProductAddOnGroupsQuery';
import { useProductAddOnGroupMutations } from '../hooks/useProductAddOnGroupMutations';
import { AssignAddOnGroupSchema, type AssignAddOnGroupInput } from '../schemas/productAddonGroup.schema';
import type { AddOnGroupRecord } from '../services/addonGroup.types';

interface Props {
    productId: string;
}

export function ProductAddOnGroupPanel({ productId }: Props) {
    const { data = [], isLoading } = useProductAddOnGroupsQuery(productId);
    const { assignMutation, removeMutation } = useProductAddOnGroupMutations(productId);
    const [showForm, setShowForm]       = useState(false);
    const [formError, setFormError]     = useState<string | null>(null);
    const [selected, setSelected]       = useState<AddOnGroupRecord | null>(null);
    const [removeTarget, setRemoveTarget] = useState<{ id: string; name: string } | null>(null);

    const { handleSubmit, setValue, formState: { errors } } = useForm<AssignAddOnGroupInput>({
        resolver: zodResolver(AssignAddOnGroupSchema),
    });

    const assignedIds = data.map((g) => g.id);

    const handleAssign = (payload: AssignAddOnGroupInput) => {
        assignMutation.mutate(payload, {
            onSuccess: () => {
                setShowForm(false);
                setFormError(null);
                setSelected(null);
            },
            onError: (err) => {
                if (isApiError(err) && err.status === 422) {
                    setFormError(err.message || 'Cannot assign this optional extra group.');
                } else {
                    setFormError(isApiError(err) ? err.message : 'Failed to assign group.');
                }
            },
        });
    };

    const handleRemove = (groupId: string) => {
        const group = data.find((g) => g.id === groupId);
        const name  = group?.name ?? 'this group';
        setRemoveTarget({ id: groupId, name });
    };

    const handleConfirmRemove = () => {
        if (removeTarget) removeMutation.mutate(removeTarget.id);
        setRemoveTarget(null);
    };

    const handleCancel = () => {
        setShowForm(false);
        setFormError(null);
        setSelected(null);
    };

    if (isLoading) return <p className="text-sm text-mp-muted">Loading optional extra groups…</p>;

    return (
        <PermissionGuard permission={{ resource: 'smr', action: 'product-addon-group.browse' }}>
            <div className="space-y-4">
                {data.length === 0 ? (
                    <p className="text-sm text-mp-muted">No optional extra groups assigned.</p>
                ) : (
                    <ProductAddOnGroupDataTable data={data} onRemove={handleRemove} />
                )}

                {!showForm && (
                    <PermissionGuard permission={{ resource: 'smr', action: 'product-addon-group.add' }}>
                        <div className="border-t border-mp-border pt-4">
                            <Button
                                size="sm"
                                variant="primary"
                                type="button"
                                onClick={() => setShowForm(true)}
                            >
                                Assign Group
                            </Button>
                        </div>
                    </PermissionGuard>
                )}

                {showForm && (
                    <div className="border-t border-mp-border pt-4">
                        <h3 className="font-semibold mb-3">Assign Optional Extra Group</h3>
                        <form
                            onSubmit={handleSubmit(handleAssign)}
                            className="flex flex-col gap-3"
                        >
                            <AddOnGroupCombobox
                                value={selected}
                                onChange={(group) => {
                                    setSelected(group);
                                    setValue('addon_group_id', group?.id ?? '');
                                    setFormError(null);
                                }}
                                excludeIds={assignedIds}
                                disabled={assignMutation.isPending}
                                error={errors.addon_group_id?.message ?? formError}
                            />
                            <div className="flex gap-2">
                                <Button
                                    type="submit"
                                    size="sm"
                                    variant="primary"
                                    disabled={assignMutation.isPending}
                                >
                                    {assignMutation.isPending ? 'Assigning…' : 'Assign'}
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={handleCancel}
                                    disabled={assignMutation.isPending}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </div>
                )}

                {removeTarget && (
                    <ConfirmActionModal
                        isOpen={true}
                        title="Remove optional extra group"
                        message={`Remove "${removeTarget.name}" from this product? Existing optional extras created by this group will remain assigned.`}
                        confirmLabel="Remove"
                        confirmVariant="danger"
                        isPending={removeMutation.isPending}
                        onConfirm={handleConfirmRemove}
                        onClose={() => setRemoveTarget(null)}
                    />
                )}
            </div>
        </PermissionGuard>
    );
}
